import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import GradientText from "@/components/home/primitives/GradientText";
import SectionHeading from "@/components/home/primitives/SectionHeading";
import { USE_CASES } from "@/data/marketing/homepage";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

export default function HomeFeatures() {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();

  return (
    <section
      id="use-cases"
      className="relative w-full overflow-hidden bg-[#0A0E1A] py-20 sm:py-24 md:py-28"
    >
      {/* Ambient brand auras drifting behind the tiles — the same glow vocabulary as the hero. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-0">
        <div className="absolute -top-24 left-1/2 h-[38rem] w-[38rem] -translate-x-1/2 rounded-full bg-[#4E9CFF]/20 blur-[120px] animate-breathe motion-reduce:animate-none" />
        <div className="absolute -bottom-32 -left-24 h-[30rem] w-[30rem] rounded-full bg-[#FF4DB8]/15 blur-[120px] animate-breathe motion-reduce:animate-none [animation-delay:1.5s]" />
        <div className="absolute -bottom-24 right-0 h-[26rem] w-[26rem] rounded-full bg-[#4E9CFF]/12 blur-[110px] animate-breathe motion-reduce:animate-none [animation-delay:3s]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          tone="dark"
          eyebrow="Use cases"
          title={
            <>
              Where businesses <GradientText>point it</GradientText>
            </>
          }
          subtitle="Internal support. External support. The same genie, trained once on what you do, answering customers and staff across every channel they actually use."
        />

        <div ref={ref} className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {USE_CASES.map((useCase, i) => (
            <div
              key={useCase.id}
              className={cn(
                "group relative transition-all duration-700 ease-out",
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              {/* Signature gradient border — faint at rest, ignites on hover. */}
              <div className="relative rounded-[24px] bg-gradient-to-br from-[#4E9CFF]/40 via-white/5 to-[#FF4DB8]/40 p-[1.5px] transition-all duration-500 group-hover:from-[#4E9CFF] group-hover:to-[#FF4DB8] group-hover:shadow-[0_30px_90px_-30px_rgba(78,156,255,0.55)]">
                <div className="flex h-full flex-col overflow-hidden rounded-[23px] bg-[#0F1526] transition-transform duration-500 ease-out group-hover:-translate-y-1.5">
                  {/* Cinematic photo — the imagery is the hero, not a thin strip. */}
                  <div className="relative h-60 w-full overflow-hidden sm:h-64">
                    <img
                      src={useCase.image}
                      alt={useCase.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.07]"
                    />
                    {/* Scrim: melds the photo into the card body and lifts the title off the image. */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F1526] via-[#0F1526]/40 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/25 to-transparent" />

                    {/* Category kicker — icon + label, so it tells you who the card is for. */}
                    <span className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-[#0A0E1A]/70 py-1.5 pl-1.5 pr-3.5 shadow-lg shadow-black/30 ring-1 ring-white/15 backdrop-blur-md">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-sm shadow-black/20 ring-1 ring-white/40">
                        <GenieIcon name={useCase.genieIcon} size={16} className="h-4 w-4" />
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-wide text-white">
                        {useCase.kicker}
                      </span>
                    </span>

                    {/* Title set over the image, cinematic-poster style. */}
                    <h3 className="absolute inset-x-5 bottom-4 text-xl font-semibold leading-snug text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
                      {useCase.title}
                    </h3>
                  </div>

                  {/* Content shelf — every word of copy stays visible. */}
                  <div className="flex flex-1 flex-col p-6 sm:p-7">
                    <p className="text-sm leading-relaxed text-white/65">
                      {useCase.description}
                    </p>
                    <ul className="mt-5 space-y-2.5 border-t border-white/10 pt-5">
                      {useCase.points.map((point) => (
                        <li key={point} className="flex items-start gap-2.5">
                          <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" />
                          <span className="text-sm leading-relaxed text-white/80">
                            {point}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={useCase.href}
                      className="mt-6 inline-flex items-center text-sm font-semibold text-genie-primary transition-colors hover:text-genie-secondary"
                    >
                      {useCase.ctaLabel}
                      <ChevronRight className="ml-0.5 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
